import { formatDate } from "@/helpers/formatDate";
import { getIngredients } from "@/services/ingredients/api";
import { getPayments } from "@/services/payment/api";
import { Payment } from "@/types/payment";
import { QueryParamsBase } from "@/types/types";
import { notification } from "antd";
import ExcelJS from "exceljs";
import { useState } from "react";
import { paymentMethodLabel } from "../PaymentsPrivate";
import { formatPrice } from "@/lib/utils";

type UseExportPaymentsProps = {
  tableParams?: QueryParamsBase;
};

export const useExportPayments = (props?: UseExportPaymentsProps) => {
  const tableParams = props?.tableParams;
  const [api, contextHolder] = notification.useNotification();
  const [loadingButtonExport, setLoadingButtonExport] =
    useState<boolean>(false);

  const openNotification = (
    type: "success" | "error" | "info" | "warning",
    placement: "topLeft" | "topRight" | "bottomLeft",
    message: string,
    description: string,
  ) => {
    api[type]({
      message,
      description,
      duration: 2.5,
      placement,
    });
  };

  const generateExcel = async () => {
    try {
      setLoadingButtonExport(true);

      openNotification(
        "info",
        "topLeft",
        "La descarga del archivo Excel está en proceso.",
        "Te agradecemos tu paciencia.",
      );

      // Obtener todos los ingredientes aplicando filtros si existen
      const data = await getPayments({
        ...tableParams,
        pageSize: 10000,
        current: 1,
      });

      const workBook = new ExcelJS.Workbook();
      const sheet = workBook.addWorksheet("Inventario");

      const fileName = "Historal_caja.xlsx";

      // Columnas del Excel
      sheet.columns = [
        { header: "N° Ticket", key: "number_ticket", width: 15 },
        { header: "Mesa", key: "table_number", width: 10 },
        { header: "Total", key: "total", width: 15 },
        { header: "Método de pago", key: "method", width: 15 },
        { header: "Estado", key: "status", width: 15 },
        { header: "N° Factura", key: "invoice_number", width: 15 },
        { header: "Fecha de pago", key: "created_at", width: 15 },
      ];

      sheet.getRow(1).font = { bold: true };

      // Insertar filas
      (data?.items || []).forEach((item: Payment) => {
        sheet.addRow({
          number_ticket: item.number_ticket ?? "Sin n ticket",
          table_number: item.table_number ?? "Sin n mesa",
          total: formatPrice(item.total) ?? 0,
          method:
            item.method === "cash"
              ? "Efectivo"
              : item.method === "card"
                ? "Tarjeta"
                : item.method === "credit"
                  ? "Crédito"
                  : "",
          status: item.status === "paid" ? "Pagado" : "Pendiente",
          invoice_number: item.invoice_number ?? "",
          created_at: item.created_at
            ? new Date(item.created_at).toLocaleString("es-CL")
            : "Sin fecha",
        });
      });

      const buffer = await workBook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      window.URL.revokeObjectURL(url);

      openNotification(
        "success",
        "topLeft",
        "Documento descargado con éxito",
        "Ya puedes visualizar el archivo de caja",
      );

      setLoadingButtonExport(false);
    } catch (error: any) {
      console.error("Error al exportar caja:", error);

      openNotification(
        "error",
        "topLeft",
        "Error al exportar",
        "No se pudo generar el archivo Excel. Intenta nuevamente.",
      );

      setLoadingButtonExport(false);
    }
  };

  return {
    contextHolder,
    generateExcel,
    loadingButtonExport,
  };
};
